import Logo from '@assets/svgs/upercut-logo.svg';
import Button from '@components/Button';
import EmailInput from '@components/Inputs/EmailInput';
import PasswordInput from '@components/Inputs/PasswordInput';
import { PageHead } from '@components/PageHead';
import Text from '@components/Text';
import { useLogin } from '@hooks/useLogin';
import { Roles } from '@utils/roleUtils';
import { Alert, Box, Checkbox, FormControlLabel, Stack } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import './index.css';

interface LoginFormValues {
    email: string;
    password: string;
    legalAgreement: boolean;
}

export const LoginPage = () => {
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<LoginFormValues>({
        defaultValues: {
            email: '',
            password: '',
            legalAgreement: false,
        },
        mode: 'onChange',
    });

    const { login, loading } = useLogin({
        onCompleted: (data) => {
            setError(null);
        },
        onError: error => {
            setError(error.message);
        },
        // Role-based redirect
        redirectTo: (role: Roles) => {
            if (role === Roles.UPERCUT) {
                return '/'; // Upercut dashboard
            } else if ([Roles.PRO, Roles.COMPANY, Roles.FREELANCE].includes(role)) {
                return '/pro/dashboard'; // Pro dashboard (to be created)
            } else {
                return '/'; // Default dashboard
            }
        },
    });

    const onSubmit = async (values: LoginFormValues) => {
        await login(values.email, values.password);
    };

    return (
        <>
            <PageHead useDynamicTitle />
            <Stack gap={2} justifyContent="center" alignItems="center" sx={{ height: '100vh' }} maxWidth="sm" margin="auto">
                <article>
                    <img src={Logo} alt="upercut logo" />
                </article>
                <Text size={36} fontWeight={700} white heading data-testid="login-header">
                    Log in to CMS
                </Text>
                <Text size={16} white style={{ opacity: 0.8, marginTop: -8 }}>
                    For Upercut and Pro users
                </Text>
                {error && (
                    <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <form className="form" onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                        name="email"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <EmailInput
                                name="email"
                                label="Email"
                                placeholder="Entrez votre email"
                                value={field.value}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(event);
                                    if (error) setError(null);
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="password"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <PasswordInput
                                label="Mot de passe"
                                placeholder="Entrez votre mot de passe"
                                value={field.value}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    field.onChange(event);
                                    if (error) setError(null);
                                }}
                                fullWidth
                            />
                        )}
                    />
                    <Box sx={{ padding: 2 }}>
                        <Controller
                            name="legalAgreement"
                            control={control}
                            rules={{ validate: value => value === true }}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={event => {
                                                field.onChange(event.target.checked);
                                                if (error) setError(null);
                                            }}
                                            name="legalAgreement"
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Text white>
                                            En cliquant sur ce bouton, vous déclarez avoir lu, compris et accepté les
                                            mentions légales d’Upercut, ses conditions générales d’utilisation ainsi que les
                                            conditions d'utilisation des comptes connectés Stripe
                                        </Text>
                                    }
                                />
                            )}
                        />
                    </Box>
                    <Button
                        className='submit-button'
                        size="large" fullWidth type="submit" disabled={!isValid || loading}>
                        {loading ? 'Logging in...' : 'Suivant'}
                    </Button>
                    <div
                        onClick={() => {
                            navigate('/forgot-password');
                        }}
                        className="forgot-password"
                        style={{ cursor: 'pointer', marginTop: '16px', textAlign: 'center' }}
                    >
                        <Text underline white>
                            Mot de passe oublié ?
                        </Text>
                    </div>
                </form>
            </Stack >
        </>
    );
};
